import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

function PendingOrders({ user, selectedAsset, refresh }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  
  // Modal states
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  
  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  const showConfirm = (message, onConfirm) => setConfirmModal({ show: true, message, onConfirm });

  const fetchPendingOrders = async () => {
    try {
      const response = await axios.get(`${API}/pending-orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOrders(response.data || []);
    } catch (error) {
      console.error('Bekleyen emirler yüklenemedi:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingOrders();
    const interval = setInterval(fetchPendingOrders, 10000);
    return () => clearInterval(interval);
  }, [refresh]);

  const cancelOrder = async (orderId) => {
    showConfirm('Bu emri iptal etmek istediğinizden emin misiniz?', async () => {
      try {
        await axios.delete(`${API}/pending-orders/${orderId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        fetchPendingOrders();
      } catch (error) {
        showAlert('Emir iptal edilemedi: ' + (error.response?.data?.detail || error.message), 'error');
      }
    });
  };

  // Modal components
  const renderModals = () => (
    <>
      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Site İçi Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-sm mb-4 text-gray-300">{confirmModal.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
                  className="flex-1 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ show: false, message: '', onConfirm: null });
                  }}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                >
                  Evet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <>
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="text-gray-400 text-sm mt-2">Yükleniyor...</p>
        </div>
        {renderModals()}
      </>
    );
  }

  if (orders.length === 0) {
    return (
      <>
        <div className="text-center py-8">
          <p className="text-gray-400 text-sm sm:text-base">⏳ Bekleyen emir bulunmuyor</p>
          <p className="text-gray-500 text-xs sm:text-sm mt-1">
            Borsa kapalıyken verdiğiniz emirler burada görünür
          </p>
        </div>
        {renderModals()}
      </>
    );
  }

  return (
    <>
      <div className="space-y-3 max-h-[200px] sm:max-h-[400px] overflow-y-auto">
        {orders.map((order) => (
          <div
            key={order.id}
            className="bg-gray-700/50 rounded-lg p-3 sm:p-4 border border-gray-600 hover:border-gray-500 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-bold text-white text-sm sm:text-base">{order.asset_symbol || order.symbol}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    order.transaction_type === 'BUY' 
                      ? 'bg-green-900/50 text-green-400' 
                      : 'bg-red-900/50 text-red-400'
                  }`}>
                    {order.transaction_type === 'BUY' ? 'ALIŞ' : 'SATIŞ'}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-900/50 text-yellow-400">
                    BEKLİYOR
                  </span>
                </div>
                <div className="text-xs sm:text-sm text-gray-400">
                  {order.asset_name || order.asset_symbol || order.symbol}
                </div>
              </div>
              <button
                onClick={() => cancelOrder(order.id)}
                className="text-red-400 hover:text-red-300 p-1 hover:bg-red-900/20 rounded transition-colors"
                title="İptal Et"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
              <div>
                <span className="text-gray-400">Miktar:</span>
                <span className="text-white font-mono ml-1 sm:ml-2">{order.amount?.toFixed(4)}</span>
              </div>
              <div>
                <span className="text-gray-400">Fiyat:</span>
                <span className="text-white font-mono ml-1 sm:ml-2">
                  {formatCurrency(order.limit_price || order.price, user)}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Toplam:</span>
                <span className="text-white font-mono ml-1 sm:ml-2">
                  {formatCurrency(order.amount * (order.limit_price || order.price), user)}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Tarih:</span>
                <span className="text-white font-mono ml-1 sm:ml-2">
                  {new Date(order.created_at).toLocaleDateString('tr-TR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>

            {order.note && (
              <div className="mt-2 pt-2 border-t border-gray-600">
                <span className="text-gray-400 text-xs">Not:</span>
                <p className="text-white text-xs mt-1">{order.note}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {renderModals()}
    </>
  );
}

export default PendingOrders;
